<template>
  <div id="log-in-view" class="app-page__container">
    <h3 class="title is-3 has-text-centered u-m-y-large">Welcome back.</h3>
    <form class="log-in-form" @submit.prevent="handleLogInWithEmailAndPassword">
      <b-field v-if="logInError">
        <b-message type="is-danger">
          {{ logInError.message }}
        </b-message>
      </b-field>
      <b-field
        label="Email"
        :type="formErrors[logInForm.userEmail.name] ? 'is-danger' : ''"
        :message="formErrors[logInForm.userEmail.name] || ''"
      >
        <b-input
          v-model="logInForm.userEmail.value"
          :name="logInForm.userEmail.name"
          :disabled="isLogInFormInProgress"
        />
      </b-field>
      <b-field
        label="Password"
        :type="formErrors[logInForm.userPassword.name] ? 'is-danger' : ''"
        :message="formErrors[logInForm.userPassword.name] || ''"
      >
        <b-input
          v-model="logInForm.userPassword.value"
          type="password"
          :name="logInForm.userPassword.name"
          password-reveal
          :disabled="isLogInFormInProgress"
        />
      </b-field>
      <b-button
        native-type="submit"
        type="is-primary"
        value="Log In"
        class="is-fullwidth"
        :loading="isLogInWithEmailInProgress"
        :disabled="isLogInFormInProgress"
      >
        Log In
      </b-button>
      <div class="u-p-t has-text-centered">
        <b-button type="is-text" size="is-small" @click="$router.push('/reset-password')">
          Forgot Password?
        </b-button>
      </div>
      <div class="content-divider" data-content="OR" />
      <b-button
        type="is-dark"
        outlined
        class="is-fullwidth"
        @click.prevent="handleLoginWithGoogle"
        :loading="isLogInWithGoogleInProgress"
        :disabled="isLogInFormInProgress"
      >
        <span class="google-logo__container">
          <img
            src="@/assets/images/google-logo-icon-60-by-60.png"
            alt="Google Logo"
            class="google-logo__img"
          />
        </span>
        &nbsp;
        Log In With Google
      </b-button>
    </form>
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations,
} from 'vuex';
import * as collections from '@/constants/dbCollections';
import * as ValidationService from '@/services/Validation.service';
import * as DatabaseService from '@/services/Database.service';
import * as AuthActions from '@/store/actions/Auth.actions';
import * as SubscriptionActions from '@/store/actions/Subscription.actions';
import * as SubscriptionMutations from '@/store/mutations/Subscription.mutations';
import * as WorkspaceActions from '@/store/actions/Workspace.actions';
import * as WorkspaceMutations from '@/store/mutations/Workspace.mutations';
import { isObjectEmpty } from '@/helpers/dataHelpers';

export default {
  name: 'log-in-view',

  data() {
    return {
      logInForm: {
        userEmail: {
          name: 'Email Address',
          value: '',
          required: true,
          validator: ValidationService.validateEmail,
        },
        userPassword: {
          name: 'Password',
          value: '',
          required: true,
        },
      },
      formErrors: {},
      isLogInWithGoogleInProgress: false,
      isLogInWithEmailInProgress: false,
    };
  },

  computed: {
    ...mapGetters([
      'loggedInUser',
    ]),
    ...mapState({
      logInError: state => state.auth.logInError,
      subscriptions: state => state.subscription.subscriptions,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    logInPayload() {
      return {
        userEmail: this.logInForm.userEmail.value,
        userPassword: this.logInForm.userPassword.value,
      };
    },
    isLogInFormInProgress() {
      return this.isLogInWithEmailInProgress || this.isLogInWithGoogleInProgress;
    },
  },

  methods: {
    ...mapActions({
      logInWithEmailAndPassword: AuthActions.LOG_IN_WITH_EMAIL_AND_PASSWORD,
      logInWithGoogleAuth: AuthActions.LOG_IN_WITH_GOOGLE_AUTH,
      fetchWorkspaceById: WorkspaceActions.FETCH_WORKSPACE_BY_ID,
      fetchSubscriptionByWorkspaceId: SubscriptionActions.FETCH_SUBSCRIPTION_BY_WORKSPACE_ID,
    }),
    ...mapMutations({
      setActiveWorkspace: WorkspaceMutations.SET_ACTIVE_WORKSPACE,
      setActiveWorkspaceSubscription: SubscriptionMutations.SET_ACTIVE_WORKSPACE_SUBSCRIPTION,
    }),
    async subscribeToWorkspaceUsersChangeEvents() {
      this.unsubscribeWorkspaceUsersListener = await DatabaseService
        .getDocumentReference(collections.WORKSPACE_USERS, this.loggedInUser.workspaceId)
        .onSnapshot(this.handleWorkspaceUsersUpdate);
    },
    async handleLogInWithEmailAndPassword() {
      this.formErrors = ValidationService.runFormValidation({ ...this.logInForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isLogInWithEmailInProgress = true;
      try {
        await this.logInWithEmailAndPassword(this.logInPayload);

        if (this.loggedInUser.workspaceId) {
          await this.fetchWorkspaceById({ workspaceId: this.loggedInUser.workspaceId });
          this.setActiveWorkspace({ workspaceId: this.loggedInUser.workspaceId });
          await this.fetchSubscriptionByWorkspaceId({ workspaceId: this.activeWorkspaceId });
          this.setActiveWorkspaceSubscription({ subscriptionId: Object.keys(this.subscriptions)[0] });
          this.$router.push('/dashboard');
        } else {
          this.$router.push('/onboarding');
        }
        this.$emit('loginSuccess');
        this.isLogInWithEmailInProgress = false;
      } catch {
        this.isLogInWithEmailInProgress = false;
      }
    },
    handleLoginWithGoogle() {
      this.isLogInWithGoogleInProgress = true;
      this.logInWithGoogleAuth();
    },
  },
};
</script>

<style lang="scss" scoped>
.log-in-form {
  margin: 0 auto;
  @media screen and (min-width: $tablet) {
    max-width: 500px;
  }
}
</style>
